import { fixedUi } from '@/i18n/fixedUi';
/**
 * SHEIN 营销活动报名：组装 register-campaign 的 goodsInfoList。
 *
 * - partakeScope 0=SKC / 1=SKU
 * - adjustForm 1=活动价格 / 6=售价降幅 / 0=无调价（不报价）
 * - 自运营：siteInfoList；半托管/全托管：supplyPriceInfo
 * - partakeFieldList：1 价格/降幅，2 活动件数，3 预留件数；缺失且 adjustForm=0 时只报 SKC/SKU
 */

function firstText(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toPositiveInt(value) {
  const n = toFiniteNumber(value);
  return n != null && n > 0 ? Math.trunc(n) : null;
}

function toNonNegInt(value) {
  const n = toFiniteNumber(value);
  return n != null && n >= 0 ? Math.trunc(n) : null;
}

function roundMoney(value, currency) {
  const n = toFiniteNumber(value);
  if (n == null) return null;
  const cur = String(currency || '').toUpperCase();
  if (cur === 'JPY') {
    return String(Math.round(n));
  }
  return (Math.round(n * 100) / 100).toFixed(2);
}

/** 从站点规则取普通档最低供货/售价降幅（优先 ordinary） */
export function pickOrdinaryMinRate(siteRule) {
  if (!siteRule || typeof siteRule !== 'object') return null;
  const ordinary = toFiniteNumber(siteRule.minSupplyPriceRate)
    ?? toFiniteNumber(siteRule.minSalePriceRate);
  if (ordinary != null) return ordinary;
  return toFiniteNumber(siteRule.minVipSupplyPriceRate)
    ?? toFiniteNumber(siteRule.minSvipSupplyPriceRate);
}

export function extractSkcSiteRules(product) {
  const rule = product?.partakeRuleInfo;
  const list = Array.isArray(rule?.siteRuleInfoList) ? rule.siteRuleInfoList : [];
  return list;
}

export function extractSkuSiteRules(sku) {
  const rule = sku?.partakeRuleInfo;
  const list = Array.isArray(rule?.siteRuleInfoList) ? rule.siteRuleInfoList : [];
  return list;
}

export function extractSkcPriceInfoList(product) {
  return Array.isArray(product?.priceInfoList) ? product.priceInfoList : [];
}

export function extractSkuPriceInfoList(sku) {
  return Array.isArray(sku?.priceInfoList) ? sku.priceInfoList : [];
}

function findPriceForSite(priceInfoList, site) {
  const list = Array.isArray(priceInfoList) ? priceInfoList : [];
  if (!list.length) return null;
  if (!site) return list[0];
  const matched = list.find((p) => firstText(p?.site) === String(site));
  return matched || list[0];
}

function findRuleForSite(siteRules, site) {
  const list = Array.isArray(siteRules) ? siteRules : [];
  if (!list.length) return null;
  if (!site) {
    return list.find((r) => !firstText(r?.site)) || list[0];
  }
  return list.find((r) => firstText(r?.site) === String(site)) || list[0];
}

function sellCurrencyForSite(detail, site) {
  const list = Array.isArray(detail?.sellPriceSiteInfoList) ? detail.sellPriceSiteInfoList : [];
  const hit = list.find((s) => firstText(s?.site) === String(site));
  return firstText(hit?.currency) || '';
}

function resolveSelectedSites(detail, selectedSites, supportSelect) {
  const all = Array.isArray(detail?.siteList)
    ? detail.siteList.map((s) => String(s).trim()).filter(Boolean)
    : [];
  if (!supportSelect) return all;
  const picked = Array.isArray(selectedSites)
    ? selectedSites.map((s) => String(s).trim()).filter(Boolean)
    : [];
  if (!picked.length) {
    throw new Error('请选择报名站点');
  }
  const allow = new Set(all);
  const invalid = picked.filter((s) => allow.size > 0 && !allow.has(s));
  if (invalid.length) {
    throw new Error(`站点不在可报范围：${invalid.join('、')}`);
  }
  return picked;
}

/**
 * 界面「折扣」= 中文几折（99 → 0.99 × 基准价，1 → 0.01 × 基准价）。
 * 平台规则里的 min/max*Rate 是降幅（% off），互转：几折 = 100 - 降幅。
 */
export function zheFromReduction(reduction) {
  const r = toFiniteNumber(reduction);
  return r == null ? null : 100 - r;
}

export function reductionFromZhe(zhe) {
  const z = toFiniteNumber(zhe);
  return z == null ? null : 100 - z;
}

/** 几折 → 活动价：基准价 × (几折 / 100) */
function calcCostFromRate(supplyPrice, zhe, currency) {
  const price = toFiniteNumber(supplyPrice);
  const z = toFiniteNumber(zhe);
  if (price == null || z == null) return null;
  return roundMoney(price * (z / 100), currency);
}

function calcSaleFromRate(salePrice, zhe, currency) {
  return calcCostFromRate(salePrice, zhe, currency);
}

/**
 * 活动价必须严格小于基准价（半托管=供货价，自运营=售价）。
 * @param {{ requirePositive?: boolean }} [opts] 输入过程可放宽「须大于 0」，提交时再强制
 * @returns {string|null} 错误文案；通过时 null
 */
export function validateActivityPrice(price, referencePrice, referenceLabel = fixedUi("供货价"), opts = {}) {
  const p = toFiniteNumber(price);
  if (p == null) return null;
  if (p < 0) return '活动价不能为负数';
  if (opts.requirePositive !== false && p <= 0) return '活动价须大于 0';
  const base = toFiniteNumber(referencePrice);
  if (base != null && p >= base) {
    return `活动价必须小于${referenceLabel}`;
  }
  return null;
}

/**
 * 折扣按「几折」填写：1 → 0.01×价，99 → 0.99×价；对应比例不能大于 1（填写值不能 ≥ 100）。
 * @param {{ requirePositive?: boolean }} [opts] 输入过程可放宽「须大于 0」，提交时再强制
 * @returns {string|null} 错误文案；通过时 null
 */
export function validateDiscountRate(rate, opts = {}) {
  const r = toFiniteNumber(rate);
  if (r == null) return null;
  if (r < 0) return '折扣不能为负数';
  if (opts.requirePositive !== false && r <= 0) return '折扣须大于 0';
  // 100 折 = 1×供货价，不满足「必须小于供货价」；>100 则比例大于 1
  if (r >= 100) return '折扣不能大于 1';
  return null;
}

function assertEnrollPriceAgainstBase(enrollPrice, basePrice, label, baseLabel) {
  const msg = validateActivityPrice(enrollPrice, basePrice, baseLabel);
  if (msg) throw new Error(`${label} ${msg}`);
}

function assertDiscountRate(rate, label) {
  const msg = validateDiscountRate(rate);
  if (msg) throw new Error(`${label} ${msg}`);
}

function buildSupplyPriceInfo({
  detail,
  priceInfoList,
  siteRules,
  draft,
  selectedSites,
  supportSelect,
  label,
}) {
  const currency = firstText(detail?.supplyPriceCurrency)
    || firstText(priceInfoList?.[0]?.currency)
    || 'CNY';
  const priceRow = findPriceForSite(priceInfoList, null);
  const supplyPrice = toFiniteNumber(priceRow?.supplyPrice);
  const rule = findRuleForSite(siteRules, null);
  // draft.rate 为界面几折；无草稿时用平台最低降幅换成对应几折
  const zhe = toFiniteNumber(draft?.rate) ?? zheFromReduction(pickOrdinaryMinRate(rule));
  if (zhe == null) {
    throw new Error(`${label} 请填写折扣`);
  }
  assertDiscountRate(zhe, label);
  let enrollCostPrice = firstText(draft?.price);
  if (!enrollCostPrice) {
    if (supplyPrice == null) {
      throw new Error(`${label} 缺少供货价，无法计算活动价`);
    }
    enrollCostPrice = calcCostFromRate(supplyPrice, zhe, currency);
  }
  if (!enrollCostPrice) {
    throw new Error(`${label} 请填写活动供货价`);
  }
  assertEnrollPriceAgainstBase(enrollCostPrice, supplyPrice, label, '供货价');
  const info = {
    enrollCurrency: currency,
    enrollCostPrice: String(enrollCostPrice),
  };
  if (supportSelect) {
    info.siteList = selectedSites;
  }
  return info;
}

function buildSiteInfoList({
  detail,
  priceInfoList,
  siteRules,
  draft,
  selectedSites,
  adjustForm,
  label,
}) {
  if (!selectedSites.length) {
    throw new Error(`${label} 无可报站点`);
  }
  return selectedSites.map((site) => {
    const currency = sellCurrencyForSite(detail, site)
      || firstText(findPriceForSite(priceInfoList, site)?.currency)
      || '';
    const siteDraft = draft?.sites?.[site] && typeof draft.sites[site] === 'object'
      ? draft.sites[site]
      : draft;
    const rule = findRuleForSite(siteRules, site);
    const priceRow = findPriceForSite(priceInfoList, site);
    const item = { site };
    if (currency) item.enrollCurrency = currency;

    if (Number(adjustForm) === 6) {
      // 界面填几折；平台 enrollSalePriceRate 要降幅（% off）
      const zhe = toFiniteNumber(siteDraft?.rate);
      if (zhe != null) {
        assertDiscountRate(zhe, `${label} / ${site}`);
        const reduction = reductionFromZhe(zhe);
        if (reduction == null) {
          throw new Error(`${label} / ${site} 请填写售价降幅`);
        }
        item.enrollSalePriceRate = String(reduction);
      } else {
        const reduction = pickOrdinaryMinRate(rule);
        if (reduction == null) {
          throw new Error(`${label} / ${site} 请填写售价降幅`);
        }
        item.enrollSalePriceRate = String(reduction);
      }
    } else {
      let enrollPrice = firstText(siteDraft?.price);
      const salePrice = toFiniteNumber(priceRow?.salePrice);
      if (!enrollPrice) {
        const zhe = toFiniteNumber(siteDraft?.rate) ?? zheFromReduction(pickOrdinaryMinRate(rule));
        if (zhe != null) assertDiscountRate(zhe, `${label} / ${site}`);
        if (zhe != null && salePrice != null) {
          enrollPrice = calcSaleFromRate(salePrice, zhe, currency);
        }
      }
      if (!enrollPrice) {
        throw new Error(`${label} / ${site} 请填写活动售价`);
      }
      assertEnrollPriceAgainstBase(enrollPrice, salePrice, `${label} / ${site}`, '售价');
      item.enrollPrice = String(enrollPrice);
    }
    return item;
  });
}

function applyStockFields(item, product, draft, partakeFields) {
  const needAct = partakeFields.has(2);
  const needReserve = partakeFields.has(3);
  if (needAct) {
    const actStock = toNonNegInt(draft?.actStock);
    if (actStock == null) {
      throw new Error(`SKC ${product?.skc || ''} 请填写活动件数`);
    }
    item.actStock = actStock;
  }
  if (needReserve) {
    const reserveStock = toNonNegInt(draft?.reserveStock);
    if (reserveStock == null) {
      throw new Error(`SKC ${product?.skc || ''} 请填写活动预留件数`);
    }
    item.reserveStock = reserveStock;
  }
}

/**
 * @param {{
 *   detail: object,
 *   products: object[],
 *   drafts?: Record<string, object>,
 *   selectedSites?: string[],
 *   isSelfOperated?: boolean,
 * }} args
 * @returns {object[]}
 */
export function buildSheinCampaignGoodsInfoList({
  detail,
  products,
  drafts = {},
  selectedSites = [],
  isSelfOperated = false,
}) {
  if (!detail || typeof detail !== 'object') {
    throw new Error('缺少活动详情');
  }
  const list = Array.isArray(products) ? products.filter(Boolean) : [];
  if (!list.length) {
    throw new Error('请先选择要报名的商品');
  }

  const partakeScope = Number(detail.partakeScope);
  const adjustForm = Number(detail.adjustForm);
  const partakeFields = new Set(
    (Array.isArray(detail.partakeFieldList) ? detail.partakeFieldList : [])
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n)),
  );
  const supportSelect = Number(detail.supportSelectSitePartake) === 1;
  /**
   * 无价格填报项（partakeFieldList 缺失且 adjustForm=0）的活动平台不接受报价，
   * 此时只报 SKC/SKU，不组装 supplyPriceInfo / siteInfoList。
   */
  const needPrice = partakeFields.has(1)
    || (partakeFields.size === 0 && adjustForm !== 0);
  const sites = needPrice ? resolveSelectedSites(detail, selectedSites, supportSelect) : [];

  return list.map((product) => {
    const skc = firstText(product?.skc);
    if (!skc) throw new Error('存在无效 SKC');
    const draft = drafts[skc] && typeof drafts[skc] === 'object' ? drafts[skc] : {};
    const item = { skc };
    applyStockFields(item, product, draft, partakeFields);

    if (partakeScope === 1) {
      const skuSource = Array.isArray(product?.skuInfoList) ? product.skuInfoList : [];
      if (!skuSource.length) {
        throw new Error(`SKC ${skc} 缺少 SKU 信息`);
      }
      item.skuInfoList = skuSource.map((skuRow) => {
        const sku = firstText(skuRow?.sku);
        if (!sku) throw new Error(`SKC ${skc} 存在无效 SKU`);
        const skuDraft = draft?.skus?.[sku] && typeof draft.skus[sku] === 'object'
          ? draft.skus[sku]
          : draft;
        const skuItem = { sku };
        const label = `SKC ${skc} / SKU ${sku}`;
        if (needPrice && isSelfOperated) {
          skuItem.siteInfoList = buildSiteInfoList({
            detail,
            priceInfoList: extractSkuPriceInfoList(skuRow),
            siteRules: extractSkuSiteRules(skuRow),
            draft: skuDraft,
            selectedSites: sites,
            adjustForm,
            label,
          });
        } else if (needPrice) {
          skuItem.supplyPriceInfo = buildSupplyPriceInfo({
            detail,
            priceInfoList: extractSkuPriceInfoList(skuRow),
            siteRules: extractSkuSiteRules(skuRow),
            draft: skuDraft,
            selectedSites: sites,
            supportSelect,
            label,
          });
        }
        return skuItem;
      });
      return item;
    }

    // SKC 维度
    const label = `SKC ${skc}`;
    if (needPrice && isSelfOperated) {
      item.siteInfoList = buildSiteInfoList({
        detail,
        priceInfoList: extractSkcPriceInfoList(product),
        siteRules: extractSkcSiteRules(product),
        draft,
        selectedSites: sites,
        adjustForm,
        label,
      });
    } else if (needPrice) {
      item.supplyPriceInfo = buildSupplyPriceInfo({
        detail,
        priceInfoList: extractSkcPriceInfoList(product),
        siteRules: extractSkcSiteRules(product),
        draft,
        selectedSites: sites,
        supportSelect,
        label,
      });
    }
    return item;
  });
}

export function summarizeSheinEnrollResult(res) {
  const payload = res?.info && typeof res.info === 'object' ? res.info : res;
  const successList = Array.isArray(payload?.successList) ? payload.successList : [];
  const failedList = Array.isArray(payload?.failedList) ? payload.failedList : [];
  const successCount = Number.isFinite(Number(payload?.successCount))
    ? Number(payload.successCount)
    : successList.length;
  const failCount = Number.isFinite(Number(payload?.failCount))
    ? Number(payload.failCount)
    : failedList.length;

  const failMsgs = failedList
    .map((item) => {
      const skc = firstText(item?.skc) || '';
      const reason = firstText(item?.reason, item?.failMsg, item?.msg, item?.code);
      if (!skc && !reason) return '';
      return skc ? `${skc}${reason ? `：${reason}` : ''}` : reason;
    })
    .filter(Boolean)
    .slice(0, 5);

  let message = `报名完成：成功 ${successCount}，失败 ${failCount}`;
  if (failMsgs.length) {
    message += `。失败：${failMsgs.join('；')}`;
    if (failedList.length > failMsgs.length) message += '…';
  }
  return { successCount, failCount, successList, failedList, message };
}

/** 默认草稿：平台最低降幅换算成界面几折（例：最低降幅 10% → 预填 90 折） */
export function defaultDraftFromProduct(product, detail) {
  const partakeScope = Number(detail?.partakeScope);
  const draft = { actStock: '', reserveStock: '', rate: '', price: '', skus: {}, sites: {} };
  const skcRules = extractSkcSiteRules(product);
  const skcZhe = zheFromReduction(pickOrdinaryMinRate(skcRules[0]));
  if (skcZhe != null) draft.rate = String(skcZhe);

  const minAct = toPositiveInt(product?.partakeRuleInfo?.minActStockNum
    ?? skcRules[0]?.minActStockNum);
  if (minAct != null) draft.actStock = String(minAct);

  const minReserve = toNonNegInt(product?.partakeRuleInfo?.minReserveStockNum
    ?? skcRules[0]?.minReserveStockNum);
  if (minReserve != null) draft.reserveStock = String(minReserve);

  if (partakeScope === 1) {
    const skus = Array.isArray(product?.skuInfoList) ? product.skuInfoList : [];
    skus.forEach((sku) => {
      const code = firstText(sku?.sku);
      if (!code) return;
      const zhe = zheFromReduction(pickOrdinaryMinRate(extractSkuSiteRules(sku)[0]));
      draft.skus[code] = {
        rate: zhe != null ? String(zhe) : (draft.rate || ''),
        price: '',
      };
    });
  }
  return draft;
}
